import {view} from '@sail/ui';
import {
  ChatBubbleDotCom,
  TextBody,
  useDotComFeedbackButtons,
  useDotComWordStreamer,
} from '@stripe-internal/conversations-ui';
import {useAnalytics} from '@stripe-internal/inline-data';
import {Feedback} from './Feedback';
import {ResponseContentProps} from './ResponseContent';
import {DotComSourcesDropdown} from './sources/DotComSourcesDropdown';
import {
  InlineLinkClickHandler,
  InlineLinkProps,
} from '../helpers/InlineLinkClickHandler';
import {inlineLinkClickedActionEvent} from './analytics';

export const DotComResponseContent = (
  props: ResponseContentProps & {finishedStreaming: boolean},
) => {
  const {
    // We should never have an empty response as the loading is visible when the response is empty. This is just to make TS happy.
    response = '',
    sources,
    unanswerable,
    isFailure,
    isDone,
    agentEscalation,
    streamingConversationId,
    setRenderingComplete,
    finishedStreaming,
  } = props;
  const analytics = useAnalytics();
  const feedbackButtons = useDotComFeedbackButtons();

  // Failure and agent-escalation paths do not stream text as we imitate
  // a standard Stripe avatar (and not the AI-specific one).
  // The Unanswerable path uses Support AI branding but also doesn't stream.
  // We don't stream when we've already completed rendering as well,
  // to avoid re-streaming the text on the client on re-renders.
  const streamingComplete =
    isDone === true || isFailure || agentEscalation || unanswerable;

  const wordStreamer = useDotComWordStreamer({
    text: response,
    disabled: streamingComplete,
    onFinished: () => {
      // When the streamer finishes typing, it triggers the `onDone` event through this method on the parent. This prevents the text from being re-typed on re-renders.
      setRenderingComplete(true);
    },
  });

  const getBubbleAnimation = (index: number) => {
    if (streamingComplete) {
      return 'disabled';
    }

    return index === 0 ? 'skip-scale' : 'all';
  };

  const rows = wordStreamer.textChunk
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line.length > 0)
    .map((line, i) => (
      <ChatBubbleDotCom
        // eslint-disable-next-line react/no-array-index-key
        key={`live-answers-response-${i}`}
        animation={getBubbleAnimation(i)}
        background="neutral100"
      >
        <InlineLinkClickHandler
          onClick={({href, innerText, parentInnerText}: InlineLinkProps) => {
            analytics.action(inlineLinkClickedActionEvent, {
              live_answers_source_url: href,
              live_answers_source_title: innerText,
              live_answers_source_context: parentInnerText,
            });
          }}
        >
          <TextBody body={line} background="neutral100" />
        </InlineLinkClickHandler>
      </ChatBubbleDotCom>
    ));

  return (
    <view.div
      css={{
        stack: 'y',
        gap: 'small',
        width: '100%',
      }}
    >
      {rows}
      {finishedStreaming && !isFailure && !unanswerable && sources ? (
        <view.div
          css={{
            stack: 'x',
            gap: 'small',
            width: '100%',
          }}
        >
          <view.div
            css={{
              width: 'calc(100% - 60px)',
            }}
          >
            <DotComSourcesDropdown sources={sources} />
          </view.div>
          {feedbackButtons ? (
            <Feedback streamingConversationId={streamingConversationId} />
          ) : null}
        </view.div>
      ) : null}
    </view.div>
  );
};
